import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useAuthContext } from '../../contexts/authContext';
import useSnackbar from '../../hooks/useSnackbar';
import { Severity } from '../../types';
import { FFTable, FFTableColumn } from '../../components/FFTable';
import {
  ListFilterBar,
  ListFilters,
  PAST_DATE_OPTIONS,
  SelectFilter,
} from '../../components/ListFilterBar/ListFilterBar';
import {
  getAllPaymentRequests,
  removeBillFromPaymentRequest,
} from '../../services/financeService';
import { PaymentRequest, PaymentRequestsSummary } from './types/finance.types';
import PaymentsSummaryCards, { rowBalance } from './components/PaymentsSummaryCards';
import PaymentExpandableContent from './components/PaymentExpandableContent';
import MoveBillDialog from './components/MoveBillDialog';
import { FairFareQueryParams } from 'types/interfaces/shared';
import { ContextPanel } from '../../components/ContextPanel';
import { BillDetailsView } from '../../pages/Claims/BillDetailsView';
import { BillWithClaim, getBillById } from '../../services/billsService';
import { DownloadAsExcel } from 'components/Controllers/DownloadAsExcel/DownloadAsExcel';
import TenantSelector from '../../components/TenantSelector';

type PaymentsPageData = {
  entities: PaymentRequest[];
  isLoading: boolean;
  error: null | Error;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

type QueryState = {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

type PaymentsFilters = ListFilters & { status: string };

const DEFAULT_PAYMENTS_FILTERS: PaymentsFilters = {
  search: '',
  status: '',
  datePreset: 'all',
};

// The payment request's own status (ChargeStatus), not one derived from its bills.
export const PAYMENT_STATUS_OPTIONS = [
  { value: 'Open', label: 'Open' },
  { value: 'Locked', label: 'Locked' },
  { value: 'Invoiced', label: 'Invoiced' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Void', label: 'Void' },
];

const PAYMENTS_SELECTS: SelectFilter[] = [
  {
    key: 'status',
    id: 'payments-status',
    label: 'Status',
    options: [{ value: '', label: 'All statuses' }, ...PAYMENT_STATUS_OPTIONS],
  },
];

const listQuery = (f: PaymentsFilters) => ({
  fromDate: f.fromDate,
  untilDate: f.untilDate,
  status: f.status || undefined,
  filters: { search: f.search },
});

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const PaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [inProgress, setInProgress] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<number>(0);
  const [pageData, setPageData] = useState<PaymentsPageData>({
    entities: [],
    isLoading: true,
    error: null,
    pagination: {
      page: 1,
      pageSize: 100,
      total: 0,
      totalPages: 0,
    },
  });
  const [queryState, setQueryState] = useState<QueryState>({
    page: 1,
    pageSize: 100,
    sortBy: 'paymentDueDate',
    sortOrder: 'desc',
  });
  const [filters, setFilters] = useState<PaymentsFilters>(DEFAULT_PAYMENTS_FILTERS);
  // Only the first load replaces the page with a loading message; after that
  // the filter bar stays mounted so a search never loses its cursor.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [summary, setSummary] = useState<PaymentRequestsSummary | undefined>();
  const [detailedBill, setDetailedBill] = useState<BillWithClaim | null>(null);
  const show = useSnackbar();

  // Bill details panel state
  const [showBillDetails, setShowBillDetails] = useState(false);

  // Move bill dialog state
  const [moveBillDialog, setMoveBillDialog] = useState({
    open: false,
    billId: '',
    paymentRequestId: '',
  });

  const { role } = user || { role: '' };

  const columns: FFTableColumn<PaymentRequest>[] = [
    {
      id: 'requestId',
      label: t('Payment ID'),
      width: 150,
      type: 'text',
      filterable: true,
    },
    {
      id: 'createdAt',
      label: t('Created At'),
      type: 'date',
      sortable: true,
      renderCell: (value) => {
        return new Date(value).toLocaleDateString();
      },
    },
    {
      id: 'totalBilled',
      label: t('Total Billed'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{formatter.format(value)}</span>;
      },
    },
    {
      id: 'totalSaving',
      label: t('Total Saving'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{formatter.format(value)}</span>;
      },
    },
    {
      id: 'totalAllowed',
      label: t('Total Allowed'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{formatter.format(value)}</span>;
      },
    },
    {
      id: 'totalCostContainmentFees',
      label: t('Total Fee'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{formatter.format(value)}</span>;
      },
    },
    {
      id: 'totalPayment',
      label: t('Total Payment'),
      type: 'text',
      sortable: false,
      renderCell: (value, row) => {
        return (
          <span>
            {formatter.format(
              Number(row.totalAllowed) + Number(row.totalCostContainmentFees)
            )}
          </span>
        );
      },
    },
    {
      id: 'totalPaid',
      label: t('Total Paid'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{formatter.format(value)}</span>;
      },
    },
    {
      // What is still owed on this request; Paid and Void owe nothing.
      id: 'balance',
      label: t('Balance'),
      type: 'text',
      sortable: false,
      renderCell: (value, row) => <span>{formatter.format(rowBalance(row))}</span>,
    },
    {
      id: 'status',
      label: t('Status'),
      type: 'text',
      sortable: true,
      renderCell: (value) => <span>{value}</span>,
    },
    {
      id: 'paymentStatus',
      label: t('Bills Status'),
      type: 'text',
      sortable: false,
      renderCell: (value, row) => {
        const verdictStatusToCount: { [key: string]: number } = {};
        row.bills?.forEach(({ bill }) => {
          if (bill?.verdict?.verdict) {
            const verdict = bill.verdict.verdict;
            if (verdictStatusToCount[verdict]) {
              verdictStatusToCount[verdict] += 1;
            } else {
              verdictStatusToCount[verdict] = 1;
            }
          }
        });
        const totalBills = row.bills?.length || 0;
        if (totalBills === 0) {
          return <span>No Bills</span>;
        }
        if (verdictStatusToCount['pending'] === totalBills) {
          return <span>Pending</span>;
        }
        if (verdictStatusToCount['denied'] === totalBills) {
          return <span>Denied</span>;
        }
        if (verdictStatusToCount['processing'] === totalBills) {
          return <span>Pending payment</span>;
        }
        if (verdictStatusToCount['processed'] === totalBills) {
          return <span>Pending payment confirmation</span>;
        }
        if (verdictStatusToCount['approved'] === totalBills) {
          return <span>Approved</span>;
        }
        if (verdictStatusToCount['paid'] === totalBills) {
          return <span>Paid</span>;
        }
        // Mixed statuses
        const statusParts = [];
        if (verdictStatusToCount['approved']) {
          statusParts.push(`Approved: ${verdictStatusToCount['approved']}`);
        }
        if (verdictStatusToCount['denied']) {
          statusParts.push(`Denied: ${verdictStatusToCount['denied']}`);
        }
        if (verdictStatusToCount['pending']) {
          statusParts.push(`Pending: ${verdictStatusToCount['pending']}`);
        }
        return <span>{statusParts.join(', ')}</span>;
      },
    },
    {
      //   type: 'action',
      //   id: 'downloadExcelAction',
      //   label: t('Download As Excel'),
      type: 'custom' as const,
      id: 'downloadExcelAction',
      label: t('Export Options'),
      width: 225,
      renderCell: (value: any, row: PaymentRequest) => {
        const generateData = () => {
          if (!row.bills) return [];
          const billsData = row.bills
            .filter((billRef) => billRef.bill)
            .map((billRef) => ({
              'Bill ID': billRef.billId,
              'Bill Type': billRef.bill!.billType,
              // 'Date of Service': new Date(billRef.bill!.dateOfService).toLocaleDateString(),
              'Billed Charges': billRef.bill!.billedCharges,
              'Savings Amount': billRef.bill!.savingsAmount,
              'Cost Containment Fees': billRef.bill!.costContainmentFees,
              'Bill Allowed': billRef.bill!.payable,
              'Total Due (Include Fees)': billRef.bill!.totalDue,
              // 'Provider': billRef.bill!.providerName || '',
              // 'Claim Number': billRef.bill!.claimNumber || '',
            }));
          return billsData;
        };
        return (
          <DownloadAsExcel
            fileName={`payment-request-${row.requestId}-bills.xlsx`}
            generateData={generateData}
          />
        );
      },
    },
  ];

  const fetchDataWithQuery = useCallback(
    async (query: QueryState) => {
      try {
        setPageData((prev) => ({ ...prev, isLoading: true }));

        const params: FairFareQueryParams = {
          page: query.page,
          pageSize: query.pageSize,
          sortBy: query.sortBy,
          sortOrder: query.sortOrder,
          ...listQuery(filters),
        };

        const response = await getAllPaymentRequests(params, currentTenant || undefined);

        setPageData({
          entities: response.paymentRequests,
          isLoading: false,
          error: null,
          pagination: response.pagination,
        });
        setHasLoaded(true);
        setSummary(response.summary);

        setQueryState(query);
      } catch (error) {
        const e = error as Error;
        setPageData((prev) => ({
          ...prev,
          isLoading: false,
          error: e,
        }));
        show('Failed to load payment requests data', Severity.error);
      }
    },
    [show, filters, currentTenant]
  );

  // Any change to what is being asked for starts again from the first page.
  // Keyed on the inputs rather than the callback, whose identity follows the
  // snackbar's `show` and would otherwise reload after every message.
  useEffect(() => {
    fetchDataWithQuery({ ...queryState, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentTenant]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: newPage,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: 1,
        pageSize: newPageSize,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const handleBillAction = useCallback(
    async (billId: string, action: string, paymentRequestId: string) => {
      const paymentRequest = pageData.entities.find(
        (pr) => pr.id === paymentRequestId
      );
      const bill = paymentRequest?.bills?.find(
        (b) => b.billId === billId
      )?.bill;

      if (action === 'view-details' && bill) {
        const billWithClaims = await getBillById(billId);
        setDetailedBill(billWithClaims);
        setShowBillDetails(true);
      } else if (action === 'move-to-next-payment' && bill) {
        setMoveBillDialog({
          open: true,
          billId,
          paymentRequestId: paymentRequest.requestId,
        });
      }
    },
    [pageData.entities]
  );

  const handleMoveBillConfirm = useCallback(async () => {
    try {
      setInProgress(true);
      await removeBillFromPaymentRequest(
        moveBillDialog.paymentRequestId,
        moveBillDialog.billId
      );

      show('Bill moved to next payment successfully', Severity.success);
      setMoveBillDialog({
        open: false,
        billId: '',
        paymentRequestId: '',
      });

      // Refresh data
      await fetchDataWithQuery(queryState);
    } catch (error) {
      show('Failed to move bill to next payment', Severity.error);
    } finally {
      setInProgress(false);
    }
  }, [moveBillDialog, show, queryState, fetchDataWithQuery]);

  if (!user) {
    return <div>{t('Loading')}..</div>;
  }

  if (!hasLoaded && pageData.isLoading) {
    return <div>{t('Loading')}..</div>;
  }

  if (pageData.error) {
    return (
      <div>
        {t('Error loading payment requests data')}: {pageData.error.message}
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: '100vw',
        margin: '0 auto',
        paddingTop: '10px',
        overflow: 'auto',
      }}
    >
      <Box mx={2}>
        <PaymentsSummaryCards summary={summary} />
        <ListFilterBar
          filters={filters}
          defaults={DEFAULT_PAYMENTS_FILTERS}
          onChange={setFilters}
          total={pageData.pagination.total}
          noun="payments"
          searchPlaceholder="Search by payment ID or bill ID"
          selects={PAYMENTS_SELECTS}
          dateLabel="Created"
          dateOptions={PAST_DATE_OPTIONS}
          tenantSlot={
            role === 'super_user' ? (
              <TenantSelector
                value={currentTenant}
                onChange={(val) => setCurrentTenant(val)}
                label={t('Tenant')}
                compact
              />
            ) : undefined
          }
        />
      </Box>
      <FFTable
        columns={columns}
        rows={pageData.entities}
        pagination={{
          page: pageData.pagination.page,
          pageSize: pageData.pagination.pageSize,
          total: pageData.pagination.total,
          totalPages: pageData.pagination.totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [50, 100, 200],
        }}
        serverSide={true}
        loading={pageData.isLoading}
        error={pageData.error}
        // Headers used to move the arrow without re-sorting anything.
        onSortChange={(orderBy, order) => {
          fetchDataWithQuery({ ...queryState, page: 1, sortBy: orderBy, sortOrder: order });
        }}
        initialSort={{
          orderBy: queryState.sortBy || 'paymentDueDate',
          order: queryState.sortOrder || 'desc',
        }}
        rowKey="id"
        emptyStateMessage={t('No payment requests found')}
        errorStateMessage={t('Error loading payment requests data')}
        loadingStateMessage={t('Loading payment requests data...')}
        expandableRows={{
          isRowExpandable: (row) => {
            return Boolean(
              row.bills && Array.isArray(row.bills) && row.bills.length > 0
            );
          },
          renderExpandedContent: (row) => {
            return (
              <PaymentExpandableContent
                paymentRequest={row}
                onBillAction={handleBillAction}
                loading={inProgress}
              />
            );
          },
        }}
      />

      <ContextPanel
        open={showBillDetails}
        onClose={() => {
          setShowBillDetails(false);
          setDetailedBill(null);
        }}
        side="right"
        size="large"
        title={
          detailedBill
            ? `${t('Bill Details')} - ${detailedBill.gemBillNo}`
            : t('Bill Details')
        }
      >
        {detailedBill && (
          <BillDetailsView
            bill={{
              ...detailedBill,
              billReferenceNo: detailedBill.gemBillNo,
              claimDate: detailedBill.createdAt,
              complaint: '',
              dateOfServiceFrom: detailedBill.createdAt,
              dateOfServiceTo: detailedBill.createdAt,
              billCreateDate: detailedBill.createdAt,
              billStatus: detailedBill.verdict?.verdict || 'Pending',
              billStatusExplanation: detailedBill.verdict?.reason || 'N/A',
              gemClaimNo: detailedBill.gemClaimNo || 'N/A',
              product: detailedBill.product || 'N/A',
              ClaimsBillsServiceLines:
                detailedBill.ClaimsBillsServiceLines || [],
            }}
          />
        )}
      </ContextPanel>

      <MoveBillDialog
        open={moveBillDialog.open}
        billId={moveBillDialog.billId}
        onConfirm={handleMoveBillConfirm}
        onCancel={() =>
          setMoveBillDialog({
            open: false,
            billId: '',
            paymentRequestId: '',
          })
        }
        loading={inProgress}
      />
    </div>
  );
};
